/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { callApi } from 'src/utils/apiCall';
import { CHECKLIST_ITEM_TYPE, CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';

const { YES } = CHECKLIST_COMPLETION_STATE;

// Module-level cache: cacheKey → activities array.
// Shared across TaskChecklistField (table cell) and ChecklistItemRow (slideout/detail)
// so both surfaces hit the same cached response when visible simultaneously.
export const _activityCache = {};

// pollRegistry: cacheKey → { callbacks: Set<fn>, intervalId, timeoutId }
// Each consumer registers its own callback so all live consumers are notified
// per tick. stopPoll removes only the calling consumer's callback; the interval
// persists until the Set is empty — safe for N table cells per lead.
export const _pollRegistry = {};

export const POLL_INTERVAL_MS = 30000;
export const POLL_WINDOW_MS = 10 * 60 * 1000;

export function fetchActivities(practiceId, leadId) {
    const cacheKey = `${practiceId}-${leadId}`;
    delete _activityCache[cacheKey];
    return callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/activities/?limit=20&offset=0`)
        .then((res) => {
            if (res.status === 200) {
                _activityCache[cacheKey] = res.data?.results || [];
            }
        })
        .catch(() => {});
}

export function startPoll(practiceId, leadId, onTick, completedAt) {
    const cacheKey = `${practiceId}-${leadId}`;
    if (_pollRegistry[cacheKey]) {
        // Interval already running — just register this consumer's callback.
        _pollRegistry[cacheKey].callbacks.add(onTick);
        return;
    }
    const callbacks = new Set([onTick]);
    const intervalId = setInterval(() => {
        fetchActivities(practiceId, leadId).then(() => {
            callbacks.forEach((cb) => cb());
        });
    }, POLL_INTERVAL_MS);

    const elapsed = Date.now() - new Date(completedAt).getTime();
    const remaining = Math.max(0, POLL_WINDOW_MS - elapsed);
    const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        delete _pollRegistry[cacheKey];
    }, remaining);

    _pollRegistry[cacheKey] = { callbacks, intervalId, timeoutId };
}

export function stopPoll(practiceId, leadId, onTick) {
    const cacheKey = `${practiceId}-${leadId}`;
    const reg = _pollRegistry[cacheKey];
    if (!reg) return;
    reg.callbacks.delete(onTick);
    if (reg.callbacks.size === 0) {
        clearInterval(reg.intervalId);
        clearTimeout(reg.timeoutId);
        delete _pollRegistry[cacheKey];
    }
}

export function extractDeliveryStatus(activities, itemType, completedAt) {
    if (!Array.isArray(activities) || activities.length === 0) return null;

    const matchVerb = itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT
        ? 'whatsapp_message_sent'
        : 'message_sent';

    const matchType = itemType === CHECKLIST_ITEM_TYPE.SMS_SENT ? 'SMS' : 'EMAIL';

    const candidates = activities.filter((a) => {
        if (a.verb !== matchVerb) return false;
        if (itemType !== CHECKLIST_ITEM_TYPE.WHATSAPP_SENT && a.action_object?.type !== matchType) return false;
        if (completedAt) {
            const diff = Math.abs(moment(a.timestamp).diff(moment(completedAt), 'minutes'));
            if (diff > 30) return false;
        }
        return true;
    });

    if (candidates.length === 0) return null;

    const act = candidates[0];

    if (itemType === CHECKLIST_ITEM_TYPE.WHATSAPP_SENT) {
        const waStatus = act.action_object?.status || 'sent';
        const priority = { read: 3, delivered: 2, sent: 1 };
        return { key: waStatus, priority: priority[waStatus] || 1 };
    }

    const eventCounts = act.action_object?.sent_messages?.flatMap((sm) => sm?.event_counts || []) || [];
    const getCount = (name) => eventCounts.find((e) => e.event === name)?.count || 0;

    if (getCount('clicked') > 0) return { key: 'clicked', priority: 4 };
    if (getCount('opened') > 0) return { key: 'opened', priority: 3 };
    if (getCount('delivered') > 0) return { key: 'delivered', priority: 2 };
    return { key: 'sent', priority: 1 };
}

// optimisticStatus: truthy value set immediately on send; overridden by real data if higher priority
export default function useDeliveryStatus(practiceId, leadId, itemType, completionState, completedAt, optimisticStatus) {
    const [status, setStatus] = useState(null);

    const shouldFetch = completionState === YES
        && leadId
        && practiceId
        && [CHECKLIST_ITEM_TYPE.EMAIL_SENT, CHECKLIST_ITEM_TYPE.WHATSAPP_SENT, CHECKLIST_ITEM_TYPE.SMS_SENT].includes(itemType);

    const resolve = useCallback((activities) => {
        const result = extractDeliveryStatus(activities, itemType, completedAt);
        setStatus(result);
    }, [itemType, completedAt]);

    useEffect(() => {
        if (!shouldFetch) return undefined;

        const cacheKey = `${practiceId}-${leadId}`;

        if (_activityCache[cacheKey]) {
            resolve(_activityCache[cacheKey]);
        } else {
            callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/activities/?limit=20&offset=0`)
                .then((res) => {
                    if (res.status === 200) {
                        _activityCache[cacheKey] = res.data?.results || [];
                        resolve(_activityCache[cacheKey]);
                    }
                })
                .catch(() => {});
        }

        const completedMs = completedAt ? new Date(completedAt).getTime() : 0;
        const withinWindow = completedMs && (Date.now() - completedMs) < POLL_WINDOW_MS;
        if (withinWindow) {
            const onTick = () => resolve(_activityCache[`${practiceId}-${leadId}`] || []);
            startPoll(practiceId, leadId, onTick, completedAt);
            return () => stopPoll(practiceId, leadId, onTick);
        }

        return undefined;
    }, [shouldFetch, practiceId, leadId, resolve, completedAt]);

    const optimisticObj = optimisticStatus ? { key: 'sent', priority: 1 } : null;
    if (status && optimisticObj) {
        return status.priority >= optimisticObj.priority ? status : optimisticObj;
    }
    return status || optimisticObj;
}
